/**
 * Shared video utility helpers used across multiple components.
 * Centralises MIME-type detection and browser playback capability checks
 * so the logic lives in one place and is easy to extend.
 */

const MIME_MAP = {
  mp4:   'video/mp4',
  webm:  'video/webm',
  ogg:   'video/ogg',
  ogv:   'video/ogg',
  m4v:   'video/mp4',
  mpeg4: 'video/mp4',
  mpeg:  'video/mpeg',
  mpg:   'video/mpeg',
  mov:   'video/quicktime',
  avi:   'video/x-msvideo',
};

/**
 * Infer a MIME type from a video URL's file extension.
 * Falls back to 'video/mp4' for unknown/missing extensions.
 * @param {string} url
 * @returns {string}
 */
export const getVideoMimeType = (url) => {
  if (!url || typeof url !== 'string') return 'video/mp4';
  const ext = url.split('?')[0].split('.').pop().toLowerCase();
  return MIME_MAP[ext] || 'video/mp4';
};

/**
 * Check whether the current browser can play a video identified by URL.
 * Uses canPlayType() for instant, error-free detection.
 * @param {string} url
 * @returns {boolean}
 */
export const isBrowserPlayableVideoUrl = (url) => {
  const mimeType = getVideoMimeType(url);
  return document.createElement('video').canPlayType(mimeType) !== '';
};

/**
 * Check whether the current browser can play a video with a known MIME type
 * (e.g. when the mime_type comes directly from an API response).
 * Returns true when mimeType is falsy so the video element is still shown.
 * @param {string|undefined} mimeType
 * @returns {boolean}
 */
export const isBrowserPlayableMimeType = (mimeType) => {
  if (!mimeType) return true;
  return document.createElement('video').canPlayType(mimeType) !== '';
};
